import createPropertySchemas from "@rentiohq/shared-frontend/dist/forms/createProperty";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
  performingSubmitActionResultSelector,
  submitActionCreator,
} from "@rentiohq/shared-frontend/dist/forms/createProperty/schema.createProperty.actions";
import { EField } from "@rentiohq/shared-frontend/dist/forms/createProperty/schema.createProperty.types";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { mergedPartialFormDataSelector } from "@rentiohq/shared-frontend/dist/redux/form/form.selectors";
import { EPropertyTypeId } from "@rentiohq/shared-frontend/dist/types/property.types";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Card,
  MultiStepForm as Form,
} from "@rentiohq/web-shared/dist/components";
import { useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchGroupLeave } from "scenes/Settlements/archiveWarning.utils";
import { formatMoney } from "scenes/Settlements/moneyText.utils";
import { settlementDisplayName } from "scenes/Settlements/settlementName.utils";
import { useCostsEnabled } from "scenes/Settlements/useCostsEnabled";
import { BooleanParam, NumberParam } from "serialize-query-params";
import { IPropertyFormProps } from "./PropertyForm.types";

export const PropertyForm = (props: IPropertyFormProps) => {
  const { isDuplicate, brokerContact, property, groupsCount } = props;

  // Router
  const [queryParams] = useQueryParams({
    parentId: NumberParam,
    group: BooleanParam,
  });

  const navigate = useNavigate();
  const store = useStore();
  const costsTabEnabled = useCostsEnabled();

  // Taking a unit out of its building (decision 5 of 2026-10-20): refused
  // while a confirmed settlement of the building names it, the server
  // refuses it too; else the broker is told first what happens to the
  // building's costs still to settle, as archiving does. A failed check
  // informs nobody and saves as before.
  const checkGroupLeave = async (formId: string): Promise<boolean> => {
    const groupId = property?.parentId;
    if (!property || groupId == null || !costsTabEnabled) return true;
    const formData = mergedPartialFormDataSelector(
      store.getState() as Parameters<typeof mergedPartialFormDataSelector>[0],
      formId,
    ) as Record<string, unknown> | undefined;
    const nextGroupId = formData?.[EField.Group];
    if (Number(nextGroupId) === groupId) return true;
    let leave;
    try {
      leave = await fetchGroupLeave({ unitId: property.id, groupId });
    } catch {
      return true;
    }
    if (leave.namedIn.length > 0) {
      confirm({
        type: "warning",
        title: getLocalizedText("properties.group_leave.blocked.title"),
        info: getLocalizedText("properties.group_leave.blocked.info", {
          settlements: leave.namedIn
            .map(settlement => `"${settlementDisplayName(settlement)}"`)
            .join(", "),
        }),
        secondaryAction: {
          title: getLocalizedText("payment_request.button.ok"),
        },
      });
      return false;
    }
    if (leave.openCosts === 0) return true;
    return new Promise<boolean>(resolve => {
      confirm({
        type: "warning",
        title: getLocalizedText("properties.group_leave.open_costs.title"),
        info: getLocalizedText("properties.group_leave.open_costs.info", {
          count: String(leave.openCosts),
          amount: formatMoney(leave.openTotal),
        }),
        primaryActions: [
          {
            title: getLocalizedText(
              "properties.group_leave.open_costs.confirm",
            ),
            onPress: () => resolve(true),
          },
        ],
        secondaryAction: {
          title: getLocalizedText("system.cancel"),
          onPress: () => resolve(false),
        },
        onClose: () => resolve(false),
      });
    });
  };

  // Duplicate property
  if (isDuplicate && property) {
    const extraData = {
      isDuplicate: true,
      property,
      brokerContact,
      groupsCount,
      epcIsRequired: false,
    };

    return (
      <Card>
        <Form
          formId={"property-duplicate"}
          schemas={createPropertySchemas(extraData)}
          withSummary={false}
          withAside={true}
          isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
            "create",
          )}
          performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
            "create",
          )}
          performingSubmitActionResultSelector={performingSubmitActionResultSelector(
            "create",
          )}
          submitActionCreator={submitActionCreator(extraData)}
          onSuccess={(newPropertyId?: number) => {
            navigate(`/properties/${newPropertyId}`);
          }}
        />
      </Card>
    );
  }

  // Update property
  if (property) {
    const extraData = {
      propertyId: property.id,
      property,
      brokerContact,
      groupsCount,
      isGroup: property.typeId === EPropertyTypeId.Group,
      epcIsRequired: false,
    };

    return (
      <Card>
        <Form
          formId={`property-update-${property.id}`}
          beforeSubmit={() => checkGroupLeave(`property-update-${property.id}`)}
          schemas={createPropertySchemas(extraData)}
          withSummary={false}
          withAside={false}
          isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
            "update",
            property.id,
          )}
          performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
            "update",
          )}
          performingSubmitActionResultSelector={performingSubmitActionResultSelector(
            "update",
          )}
          submitActionCreator={submitActionCreator(extraData, property.id)}
          onSuccess={() => {
            navigate(`/properties/${property.id}`);
          }}
        />
      </Card>
    );
  }

  // Create property
  const extraData = {
    brokerContact,
    groupsCount,
    parentId: queryParams?.parentId,
    isGroup: queryParams?.group || false,
    epcIsRequired: false,
  };

  return (
    <Card>
      <Form
        formId={"property-create"}
        schemas={createPropertySchemas(extraData)}
        withSummary={false}
        withAside={true}
        isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
          "create",
        )}
        performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
          "create",
        )}
        performingSubmitActionResultSelector={performingSubmitActionResultSelector(
          "create",
        )}
        submitActionCreator={submitActionCreator(extraData)}
        onSuccess={(newPropertyId?: number) => {
          navigate(`/properties/${newPropertyId}`);
        }}
      />
    </Card>
  );
};
